import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import type { HostToClientMessage, MessagePayload } from "@rubyplay/effw-connector";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { debugLog } from "@/utils/debug";
import { useSubscription } from "@/hooks/useSubscription";

export type SnapshotRuntimeMode = {
	enabled: boolean;
	intervalMs: number;
	quality: number;
	scale: number;
};

export type SnapshotRequestState = {
	token: number;
	surface: MessagePayload<HostToClientMessage, "SnapshotRequest">["surface"];
	surfaceId: string;
	reason?: MessagePayload<HostToClientMessage, "SnapshotRequest">["reason"];
};

export type SnapshotBridgeContextValue = {
	snapshotMode: SnapshotRuntimeMode;
	snapshotRequests: SnapshotRequestState[];
	consumeSnapshotRequest: (token: number) => void;
	forceAllHitAreaWidgetSnapshotsToken: number;
	triggerAllHitAreaWidgetSnapshots: () => void;
};

const DEFAULT_SNAPSHOT_MODE: SnapshotRuntimeMode = {
	enabled: false,
	intervalMs: 1000,
	quality: 0.82,
	scale: 1,
};

const SnapshotBridgeContext = createContext<SnapshotBridgeContextValue | null>(null);

function clampSnapshotIntervalMs(value: unknown): number {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return DEFAULT_SNAPSHOT_MODE.intervalMs;
	}

	return Math.max(50, Math.floor(numeric));
}

function clampSnapshotQuality(value: unknown): number {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return DEFAULT_SNAPSHOT_MODE.quality;
	}

	return Math.max(0, Math.min(1, numeric));
}

function clampSnapshotScale(value: unknown): number {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return DEFAULT_SNAPSHOT_MODE.scale;
	}

	return Math.max(0.1, Math.min(4, numeric));
}

export function SnapshotBridgeProvider({ children }: { children: ReactNode }) {
	const { onSnapshotMode, onSnapshotRequest } = useCommunicator();

	const [snapshotMode, setSnapshotMode] = useState<SnapshotRuntimeMode>(DEFAULT_SNAPSHOT_MODE);
	const [snapshotRequests, setSnapshotRequests] = useState<SnapshotRequestState[]>([]);
	const [forceAllHitAreaWidgetSnapshotsToken, setForceAllHitAreaWidgetSnapshotsToken] = useState(0);

	const snapshotRequestTokenRef = useRef(0);

	useSubscription(
		onSnapshotMode,
		(payload) => {
			const nextMode: SnapshotRuntimeMode = {
				enabled: !!payload.enabled,
				intervalMs: clampSnapshotIntervalMs(payload.intervalMs),
				quality: clampSnapshotQuality(payload.quality),
				scale: clampSnapshotScale(payload.scale),
			};

			debugLog("[EF] host -> SnapshotMode", nextMode);
			setSnapshotMode(nextMode);
		},
		[]
	);

	useSubscription(
		onSnapshotRequest,
		(payload) => {
			snapshotRequestTokenRef.current += 1;

			const nextRequest: SnapshotRequestState = {
				token: snapshotRequestTokenRef.current,
				surface: payload.surface,
				surfaceId: payload.surfaceId,
				reason: payload.reason,
			};

			debugLog("[EF] host -> SnapshotRequest", nextRequest);

			setSnapshotRequests((prev) => [...prev, nextRequest]);
		},
		[]
	);

	const consumeSnapshotRequest = useCallback((token: number) => {
		setSnapshotRequests((prev) => prev.filter((request) => request.token !== token));
	}, []);

	const triggerAllHitAreaWidgetSnapshots = useCallback(() => {
		debugLog("[EF] trigger all HitAreaWidget snapshots");
		setForceAllHitAreaWidgetSnapshotsToken((prev) => prev + 1);
	}, []);

	const value = useMemo<SnapshotBridgeContextValue>(
		() => ({
			snapshotMode,
			snapshotRequests,
			consumeSnapshotRequest,
			forceAllHitAreaWidgetSnapshotsToken,
			triggerAllHitAreaWidgetSnapshots,
		}),
		[
			snapshotMode,
			snapshotRequests,
			consumeSnapshotRequest,
			forceAllHitAreaWidgetSnapshotsToken,
			triggerAllHitAreaWidgetSnapshots,
		]
	);

	return <SnapshotBridgeContext.Provider value={value}>{children}</SnapshotBridgeContext.Provider>;
}

export function useSnapshotBridge(): SnapshotBridgeContextValue {
	const context = useContext(SnapshotBridgeContext);

	if (!context) {
		throw new Error("useSnapshotBridge must be used within a SnapshotBridgeProvider");
	}

	return context;
}